import { PurchaseReasonEnum } from '../constants/PurchaseReason.enum';
import { apiResponseHandler } from '../helpers/api-response-helper/api-response-helper';
import { TicketPurchaseIntentDto } from '../models/ticket-purchase-intent/TicketPurchaseIntentDto';
import { TicketPurchaseIntentInputDto } from '../models/ticket-purchase-intent/TicketPurchaseIntentInputDto.types';
import { createTicketPurchaseIntent } from '../resources/ticket-purchase-intent/ticket-purchase-intent-api';

// Written by `storeAdClickAttribution`/`markAdClickAttributionAsSameSession`
// (packages/composables/src/advertisement/adClickAttribution.ts) when the visitor clicked an ad
// linking to this content — read back here so a purchase later in the session still attributes
// back to the ad click.
type StoredAdClickAttribution = {
    adClickUid: string;
    purchaseReason: PurchaseReasonEnum;
    // Absent on entries stored before this field existed — treated as expired-unknown below.
    clickedAt?: number;
};

const AD_CLICK_ATTRIBUTION_MAX_AGE_MS = 24 * 60 * 60 * 1000;

// Consumes (and removes) the stored attribution so only the first purchase after the click is
// attributed to it, and expires entries older than 24h — including pre-clickedAt legacy entries,
// which are treated as expired-unknown and consumed once before being removed.
// Exported for apps with their own createTicketPurchaseIntent transport (cinema-website) so the
// attribution semantics live in exactly one place.
export function consumeAdClickAttribution(contentId?: number | string): Partial<TicketPurchaseIntentInputDto> {
    if (!contentId || typeof sessionStorage === 'undefined') {
        return {};
    }
    const key = `adClick-${contentId}`;
    const stored = sessionStorage.getItem(key);
    if (!stored) {
        return {};
    }
    try {
        const attribution: StoredAdClickAttribution = JSON.parse(stored);
        sessionStorage.removeItem(key);
        if (!attribution.clickedAt || Date.now() - attribution.clickedAt > AD_CLICK_ATTRIBUTION_MAX_AGE_MS) {
            return {};
        }
        return { adClickUid: attribution.adClickUid, purchaseReason: attribution.purchaseReason };
    } catch {
        sessionStorage.removeItem(key);
        return {};
    }
}

export async function onTicketButtonClick(
    showingId: number,
    purchaseReason?: PurchaseReasonEnum,
    contentId?: number | string
): Promise<TicketPurchaseIntentDto> {
    const promise = new Promise<TicketPurchaseIntentDto>((resolve) => {
        createTicketPurchaseIntent({
            showing: showingId,
            purchaseReason: purchaseReason,
            ...consumeAdClickAttribution(contentId)
        }).then((response) => {
            apiResponseHandler(
                response,
                (successResponse) => resolve(successResponse),
                () => resolve(null)
            );
        });
    });
    return await promise;
}

/**
 * If the url contains `isMarketingAssistant`, the purchaseReason will be `marketing_assistant_notification_push`
 * Defaults to `null`.
 *
 * @param searchParams
 * @param default_
 */
export function getPurchaseReasonFromUrlQuery(
    searchParams: URLSearchParams,
    default_: PurchaseReasonEnum = null
): PurchaseReasonEnum {
    return searchParams.has('isMarketingAssistant') ? PurchaseReasonEnum.MARKETING_ASSISTANT : default_;
}
